import {knowledgeBoxApi} from "@/container";
import {parseStreamEvent} from "@/core/http/streaming";
import {onUnmounted, ref} from "vue";
import {CitationEvent} from "@/knowledge-box/interfaces/bedrock";
import {KnowledgeableProduct} from "@/knowledge-box/interfaces";

interface KnowledgeableMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    citations?: CitationEvent[];
    products?: KnowledgeableProduct[];
    timestamp: Date;
    isStreaming?: boolean;
}

/**
 * Composable to handle the streaming of messages for a knowledgeable.
 *
 * @param knowledgeableId
 */
export const useKnowledgeableStream = (knowledgeableId: number) => {
    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const chatHistory = ref<KnowledgeableMessage[]>([]);

    let abortController: AbortController | null = null;
    let pendingProductsPromise: Promise<KnowledgeableProduct[] | null> | null = null;

    /**
     * Generate a unique message ID.
     */
    const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    /**
     * Add a new message to the chat history.
     */
    const addMessage = (role: 'user' | 'assistant', content: string = '', isStreaming = false): KnowledgeableMessage => {
        const message: KnowledgeableMessage = {
            id: generateMessageId(),
            role,
            content,
            citations: [],
            timestamp: new Date(),
            isStreaming
        };

        chatHistory.value.push(message);
        return message;
    };

    /**
     * Update the last assistant message with new content or citations.
     */
    const updateLastMessage = (content: string, citations: CitationEvent[] = []) => {
        const messages = chatHistory.value;
        const lastMessage = messages[messages.length - 1];

        if (lastMessage?.role === 'assistant') {
            lastMessage.content += content;
            if (citations.length > 0) {
                lastMessage.citations = [...(lastMessage.citations || []), ...citations];
            }
        }
    };

    /**
     * Request products for a given prompt.
     */
    const requestProducts = async (prompt: string, canQueryProducts: boolean = false): Promise<KnowledgeableProduct[] | null> => {
        if (!canQueryProducts) {
            return null;
        }

        try {
            return await knowledgeBoxApi().extractProducts(knowledgeableId, prompt, abortController);
        } catch {
            return null;
        }
    };

    /**
     * Finish streaming by marking the last assistant message as not streaming.
     */
    const finishStreaming = async () => {
        const messages = chatHistory.value;
        const lastMessage = messages[messages.length - 1];
        if (lastMessage?.role === 'assistant') {
            lastMessage.isStreaming = false;

            if (pendingProductsPromise) {
                const products = await pendingProductsPromise;
                if (products && products.length > 0) {
                    lastMessage.products = products;
                }
                pendingProductsPromise = null;
            }
        }
    };

    /**
     * Handle incoming stream chunks: text under `update`, sources under
     * `citation`, both parsed from the shared server-sent-events format.
     *
     * @param chunk
     */
    const handleStreamChunk = async (chunk: string) => {
        if (!chunk.trim()) return;

        const {event, data, ended} = parseStreamEvent(chunk);

        if (event === 'update' && data) {
            updateLastMessage(data);
        } else if (event === 'citation' && data) {
            try {
                const citation = JSON.parse(data);
                updateLastMessage('', [citation]);
            } catch (e) {
                console.warn('Failed to parse citation:', e);
            }
        } else if (!event && !ended && chunk.trim()) {
            updateLastMessage(chunk);
        }

        if (ended) {
            isLoading.value = false;
            await finishStreaming();
        }
    };

    /**
     * Handle errors during streaming.
     */
    const handleError = async () => {
        error.value = "Une erreur s'est produite lors de la récupération des données.";
        isLoading.value = false;
        await finishStreaming();
    };

    /**
     * Ask a question to the knowledgeable and stream the response.
     *
     * @param question
     * @param canQueryProducts
     */
    const streamAsk = async (question: string, canQueryProducts: boolean = false) => {
        const trimmedQuestion = question?.trim();
        if (!trimmedQuestion) {
            error.value = "La question ne peut pas être vide.";
            return;
        }

        await stopStream();
        error.value = null;

        addMessage('user', trimmedQuestion);
        addMessage('assistant', '', true);

        isLoading.value = true;
        abortController = new AbortController();

        if (canQueryProducts) {
            pendingProductsPromise = requestProducts(trimmedQuestion, canQueryProducts);
        }

        try {
            await knowledgeBoxApi().streamKnowledgeBox(
                knowledgeableId,
                trimmedQuestion,
                handleStreamChunk,
                handleError,
                abortController
            );
        } catch {
            await handleError();
        }
    };

    /**
     * Stop the current streaming session.
     */
    const stopStream = async () => {
        if (abortController) {
            abortController.abort();
            abortController = null;
        }
        isLoading.value = false;
        await finishStreaming();
    };

    /**
     * Clear the chat history and stop any ongoing streaming.
     */
    const clearHistory = async () => {
        await stopStream();
        chatHistory.value = [];
        error.value = null;
    };

    onUnmounted(stopStream);

    return {
        isLoading,
        error,
        chatHistory,
        addMessage,
        streamAsk,
        stopStream,
        clearHistory
    };
};
